import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import Header from '../components/Header'
import MenuBar from '../components/MenuBar'
import httpClient from '../components/helpers/httpClient'
import { addUserInfo } from '../store/user'
import { updateCompanyFunctions } from '../store/companiesPage'
import PopupDropdown from '../components/PopupDropdown'
import Label from '../components/Label'
import Button from '../components/UI/Button'
import { showNotificationPopup } from '../components/NotificationPopup'
import './SettingsPage.css'

const SETTINGS_SUBMENUS = [
  { id: 'profile', label: 'Profile' },
  { id: 'data', label: 'Data' },
]

const DOCUMENT_TYPE_SCOPES = [
  { id: 'product', name: 'Product' },
  { id: 'unit', name: 'Unit' },
  { id: 'transaction', name: 'Transaction' },
]

const DATA_SUBMENUS = [
  { id: 'tags', label: 'Tags' },
  { id: 'company-functions', label: 'Company functions' },
  { id: 'document-types', label: 'Document types' },
]

const DOCUMENT_TYPE_SCOPE_LABEL_COLORS = {
  product: 'blue',
  unit: 'green',
  transaction: 'orange',
}

const TAG_FILTERS = [
  { id: 'all', label: 'All tags' },
  { id: 'duplicates', label: 'Duplicates' },
]

const normalizeTagLabel = (value) => {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase()
}

const isSameId = (left, right) => String(left) === String(right)

function SettingsPage() {
  const dispatch = useDispatch()
  const userInfo = useSelector(state => state.user)
  const storedCompanyFunctions = useSelector(state => state.companiesPage.companyFunctions) || []

  const [activeSubmenu, setActiveSubmenu] = useState('profile')
  const [activeDataSubmenu, setActiveDataSubmenu] = useState('tags')
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    type: '',
  })
  const [profileSaving, setProfileSaving] = useState(false)
  const [isEditingProfile, setIsEditingProfile] = useState(false)

  const [tags, setTags] = useState([])
  const [companyFunctions, setCompanyFunctions] = useState(storedCompanyFunctions)
  const [documentTypesByScope, setDocumentTypesByScope] = useState({
    product: [],
    unit: [],
    transaction: [],
  })
  const [newTagValue, setNewTagValue] = useState('')
  const [newCompanyFunctionValue, setNewCompanyFunctionValue] = useState('')
  const [newDocumentTypeValues, setNewDocumentTypeValues] = useState({
    product: '',
    unit: '',
    transaction: '',
  })
  const [newDocumentTypeScope, setNewDocumentTypeScope] = useState(DOCUMENT_TYPE_SCOPES[0])
  const [editingTagId, setEditingTagId] = useState(null)
  const [editingTagValue, setEditingTagValue] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState([])
  const [mergeTargetTagId, setMergeTargetTagId] = useState('')
  const [tagFilter, setTagFilter] = useState('all')
  const [mergeSaving, setMergeSaving] = useState(false)
  const [editingFunctionId, setEditingFunctionId] = useState(null)
  const [editingFunctionValue, setEditingFunctionValue] = useState('')
  const [editingDocumentTypeId, setEditingDocumentTypeId] = useState(null)
  const [editingDocumentTypeValue, setEditingDocumentTypeValue] = useState('')
  const [loadingData, setLoadingData] = useState(true)

  useEffect(() => {
    const header = document.querySelector('#header-title')
    if (header) {
      header.classList.remove('hide')
    }
  }, [])

  useEffect(() => {
    setProfileForm({
      first_name: userInfo.first_name || '',
      last_name: userInfo.last_name || '',
      email: userInfo.email || '',
      type: userInfo.type || '',
    })
  }, [userInfo])

  useEffect(() => {
    if (activeSubmenu !== 'profile') {
      setIsEditingProfile(false)
    }
  }, [activeSubmenu])

  useEffect(() => {
    fetchSettingsData()
  }, [])

  useEffect(() => {
    setSelectedTagIds(prev => prev.filter(tagId => tags.some(tag => isSameId(tag.id, tagId))))
  }, [tags])

  useEffect(() => {
    if (selectedTagIds.length === 0) {
      if (mergeTargetTagId !== '') {
        setMergeTargetTagId('')
      }
      return
    }

    if (!selectedTagIds.some(tagId => isSameId(tagId, mergeTargetTagId))) {
      setMergeTargetTagId(selectedTagIds[0])
    }
  }, [mergeTargetTagId, selectedTagIds])

  const sortedTags = useMemo(() => {
    return [...tags].sort((a, b) => (a.tag || '').localeCompare(b.tag || ''))
  }, [tags])

  const duplicateTagGroups = useMemo(() => {
    const groups = tags.reduce((accumulator, tag) => {
      const normalized = normalizeTagLabel(tag.tag)

      if (!normalized) {
        return accumulator
      }

      if (!accumulator[normalized]) {
        accumulator[normalized] = []
      }

      accumulator[normalized].push(tag)
      return accumulator
    }, {})

    return Object.fromEntries(Object.entries(groups).filter(([, group]) => group.length > 1))
  }, [tags])

  const duplicateTagIds = useMemo(() => {
    return new Set(Object.values(duplicateTagGroups).flat().map(tag => tag.id))
  }, [duplicateTagGroups])

  const filteredSortedTags = useMemo(() => {
    if (tagFilter !== 'duplicates') {
      return sortedTags
    }

    return sortedTags.filter(tag => duplicateTagIds.has(tag.id))
  }, [duplicateTagIds, sortedTags, tagFilter])

  const selectedTags = useMemo(() => {
    return tags.filter(tag => selectedTagIds.some(tagId => isSameId(tag.id, tagId)))
  }, [selectedTagIds, tags])

  const sortedCompanyFunctions = useMemo(() => {
    return [...companyFunctions].sort((a, b) => (a.function || '').localeCompare(b.function || ''))
  }, [companyFunctions])

  const sortedDocumentTypesByScope = useMemo(() => {
    return Object.fromEntries(
      DOCUMENT_TYPE_SCOPES.map(({ id }) => [
        id,
        [...(documentTypesByScope[id] || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      ])
    )
  }, [documentTypesByScope])

  const allSortedDocumentTypes = useMemo(() => {
    return DOCUMENT_TYPE_SCOPES.flatMap(scopeItem =>
      (sortedDocumentTypesByScope[scopeItem.id] || []).map(item => ({
        ...item,
        scopeLabel: scopeItem.name,
      }))
    ).sort((a, b) => {
      const scopeCompare = (a.scopeLabel || '').localeCompare(b.scopeLabel || '')
      if (scopeCompare !== 0) return scopeCompare
      return (a.name || '').localeCompare(b.name || '')
    })
  }, [sortedDocumentTypesByScope])

  const fetchSettingsData = async () => {
    setLoadingData(true)

    try {
      const [tagsResponse, functionsResponse, productDocumentTypesResponse, unitDocumentTypesResponse, transactionDocumentTypesResponse] = await Promise.all([
        httpClient.get(import.meta.env.VITE_API_URL + '/api/tags/get'),
        httpClient.get(import.meta.env.VITE_API_URL + '/api/company/functions'),
        httpClient.get(import.meta.env.VITE_API_URL + '/api/document-types?scope=product'),
        httpClient.get(import.meta.env.VITE_API_URL + '/api/document-types?scope=unit'),
        httpClient.get(import.meta.env.VITE_API_URL + '/api/document-types?scope=transaction'),
      ])

      if (tagsResponse.status === 200) {
        setTags(Array.isArray(tagsResponse.data) ? tagsResponse.data : [])
      }

      if (functionsResponse.status === 200) {
        const nextFunctions = Array.isArray(functionsResponse.data) ? functionsResponse.data : []
        setCompanyFunctions(nextFunctions)
        dispatch(updateCompanyFunctions(nextFunctions))
      }

      setDocumentTypesByScope({
        product: productDocumentTypesResponse.status === 200 && Array.isArray(productDocumentTypesResponse.data) ? productDocumentTypesResponse.data : [],
        unit: unitDocumentTypesResponse.status === 200 && Array.isArray(unitDocumentTypesResponse.data) ? unitDocumentTypesResponse.data : [],
        transaction: transactionDocumentTypesResponse.status === 200 && Array.isArray(transactionDocumentTypesResponse.data) ? transactionDocumentTypesResponse.data : [],
      })
    } catch (error) {
      toast.error('Failed to load settings data')
    }

    setLoadingData(false)
  }

  const handleProfileFieldChange = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSaveProfile = async () => {
    if (!profileForm.first_name || !profileForm.last_name || !profileForm.email) {
      toast.error('First name, last name and email are required')
      return
    }

    setProfileSaving(true)

    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + '/api/users/update', {
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        email: profileForm.email,
      })

      if (response.status === 200) {
        const nextUser = response.data.user || response.data
        dispatch(addUserInfo(nextUser))
        setIsEditingProfile(false)
        toast.success('Profile updated successfully')
      } else {
        toast.error(response.data?.message || 'Failed to update profile')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile')
    }

    setProfileSaving(false)
  }

  const handleProfileHeaderAction = async () => {
    if (activeSubmenu !== 'profile') {
      return
    }

    if (!isEditingProfile) {
      setIsEditingProfile(true)
      return
    }

    await handleSaveProfile()
  }

  const handleCreateTag = async () => {
    const value = newTagValue.trim()

    if (!value) {
      toast.error('Tag name is required')
      return
    }

    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + `/api/tags/create?tagValue=${encodeURIComponent(value)}`)

      if (response.status === 200 || response.status === 201) {
        await fetchSettingsData()
        setNewTagValue('')
        toast.success('Tag created successfully')
      } else {
        toast.error(response.data?.message || 'Failed to create tag')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create tag')
    }
  }

  const handleStartEditTag = (tag) => {
    setEditingTagId(tag.id)
    setEditingTagValue(tag.tag || '')
  }

  const handleSaveTag = async (tagId) => {
    const value = editingTagValue.trim()

    if (!value) {
      toast.error('Tag name is required')
      return
    }

    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + '/api/tags/update', {
        id: tagId,
        tag: value,
      })

      if (response.status === 200) {
        setTags(prev => prev.map(item => item.id === tagId ? {
          ...item,
          ...(response.data || {}),
          tag: response.data?.tag || value,
          relation_counts: item.relation_counts,
          relation_count_total: item.relation_count_total,
        } : item))
        setEditingTagId(null)
        setEditingTagValue('')
        toast.success('Tag updated successfully')
      } else {
        toast.error(response.data?.message || 'Failed to update tag')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update tag')
    }
  }

  const handleDeleteTag = async (tagId) => {
    const tag = tags.find(item => item.id === tagId)
    const relationCounts = tag?.relation_counts || {}
    const relatedItemsTotal = tag?.relation_count_total || 0
    const relationSummary = [
      relationCounts.contacts ? `${relationCounts.contacts} contact${relationCounts.contacts === 1 ? '' : 's'}` : null,
      relationCounts.companies ? `${relationCounts.companies} compan${relationCounts.companies === 1 ? 'y' : 'ies'}` : null,
      relationCounts.projects ? `${relationCounts.projects} project${relationCounts.projects === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(', ')

    showNotificationPopup({
      message: relatedItemsTotal > 0
        ? `This tag has ${relationSummary} linked. Are you sure you want to delete this tag?`
        : 'No data linked to this tag. Are you sure you want to delete this tag?',
      actionButton: { text: 'Delete', color: 'var(--red)' },
      onAction: async () => {
        try {
          const response = await httpClient.delete(import.meta.env.VITE_API_URL + `/api/tags/archive/${tagId}`)

          if (response.status === 200) {
            setTags(prev => prev.filter(item => item.id !== tagId))
            toast.success('Tag deleted successfully')
          } else {
            toast.error(response.data?.message || 'Failed to delete tag')
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete tag')
        }
      },
    })
  }

  const handleToggleTagSelection = (tagId) => {
    setSelectedTagIds(prev => prev.some(id => isSameId(id, tagId)) ? prev.filter(id => !isSameId(id, tagId)) : [...prev, tagId])
  }

  const handleMergeTags = async () => {
    if (selectedTagIds.length < 2) {
      toast.error('Select at least two tags to merge')
      return
    }

    if (!mergeTargetTagId) {
      toast.error('Select the surviving tag')
      return
    }

    const mergedTags = selectedTags.filter(tag => !isSameId(tag.id, mergeTargetTagId))

    if (mergedTags.length === 0) {
      toast.error('Select at least one tag to merge into the survivor')
      return
    }

    setMergeSaving(true)

    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + '/api/tags/merge', {
        survivor_id: mergeTargetTagId,
        merged_tag_ids: mergedTags.map(tag => tag.id),
      })

      if (response.status === 200) {
        await fetchSettingsData()
        setSelectedTagIds([])
        setMergeTargetTagId('')
        toast.success('Tags merged successfully')
      } else {
        toast.error(response.data?.message || 'Failed to merge tags')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to merge tags')
    }

    setMergeSaving(false)
  }

  const handleRequestMerge = () => {
    const normalizedNames = [...new Set(selectedTags.map(tag => normalizeTagLabel(tag.tag)).filter(Boolean))]

    if (normalizedNames.length > 1) {
      showNotificationPopup({
        message: 'You are merging tags with different names. All linked data will move to the surviving tag and the others will be deleted. Continue?',
        actionButton: { text: 'Merge tags', color: 'var(--orange)' },
        onAction: handleMergeTags,
      })
      return
    }

    handleMergeTags()
  }

  const handleCreateCompanyFunction = async () => {
    const value = newCompanyFunctionValue.trim()

    if (!value) {
      toast.error('Company function name is required')
      return
    }

    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + '/api/company/functions/create', {
        function: value,
      })

      if (response.status === 200 || response.status === 201) {
        const nextItem = response.data
        const nextFunctions = [...companyFunctions, nextItem]
        setCompanyFunctions(nextFunctions)
        dispatch(updateCompanyFunctions(nextFunctions))
        setNewCompanyFunctionValue('')
        toast.success('Company function created successfully')
      } else {
        toast.error(response.data?.message || 'Failed to create company function')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create company function')
    }
  }

  const handleStartEditFunction = (item) => {
    setEditingFunctionId(item.id)
    setEditingFunctionValue(item.function || '')
  }

  const handleSaveCompanyFunction = async (functionId) => {
    const value = editingFunctionValue.trim()

    if (!value) {
      toast.error('Company function name is required')
      return
    }

    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + '/api/company/functions/update', {
        id: functionId,
        function: value,
      })

      if (response.status === 200) {
        const nextFunctions = companyFunctions.map(item => item.id === functionId ? { ...item, ...(response.data || {}), function: response.data?.function || value } : item)
        setCompanyFunctions(nextFunctions)
        dispatch(updateCompanyFunctions(nextFunctions))
        setEditingFunctionId(null)
        setEditingFunctionValue('')
        toast.success('Company function updated successfully')
      } else {
        toast.error(response.data?.message || 'Failed to update company function')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update company function')
    }
  }

  const handleDeleteCompanyFunction = async (functionId) => {
    showNotificationPopup({
      message: 'Are you sure you want to delete this company function?',
      actionButton: { text: 'Delete', color: 'var(--red)' },
      onAction: async () => {
        try {
          const response = await httpClient.delete(import.meta.env.VITE_API_URL + `/api/company/functions/archive/${functionId}`)

          if (response.status === 200) {
            const nextFunctions = companyFunctions.filter(item => item.id !== functionId)
            setCompanyFunctions(nextFunctions)
            dispatch(updateCompanyFunctions(nextFunctions))
            toast.success('Company function deleted successfully')
          } else {
            toast.error(response.data?.message || 'Failed to delete company function')
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete company function')
        }
      },
    })
  }

  const handleCreateDocumentType = async (scope) => {
    const value = (newDocumentTypeValues[scope] || '').trim()

    if (!value) {
      toast.error('Document type name is required')
      return
    }

    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + '/api/document-types/create', {
        name: value,
        scope,
      })

      if (response.status === 200 || response.status === 201) {
        setDocumentTypesByScope(prev => ({
          ...prev,
          [scope]: [...(prev[scope] || []), response.data],
        }))
        setNewDocumentTypeValues(prev => ({ ...prev, [scope]: '' }))
        toast.success('Document type created successfully')
      } else {
        toast.error(response.data?.error || 'Failed to create document type')
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create document type')
    }
  }

  const handleStartEditDocumentType = (item) => {
    setEditingDocumentTypeId(item.id)
    setEditingDocumentTypeValue(item.name || '')
  }

  const handleSaveDocumentType = async (scope, documentTypeId) => {
    const value = editingDocumentTypeValue.trim()

    if (!value) {
      toast.error('Document type name is required')
      return
    }

    try {
      const response = await httpClient.put(import.meta.env.VITE_API_URL + `/api/document-types/${documentTypeId}`, {
        name: value,
      })

      if (response.status === 200) {
        setDocumentTypesByScope(prev => ({
          ...prev,
          [scope]: (prev[scope] || []).map(item => item.id === documentTypeId ? { ...item, ...(response.data || {}), name: response.data?.name || value } : item),
        }))
        setEditingDocumentTypeId(null)
        setEditingDocumentTypeValue('')
        toast.success('Document type updated successfully')
      } else {
        toast.error(response.data?.error || 'Failed to update document type')
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update document type')
    }
  }

  const handleDeleteDocumentType = async (scope, documentTypeId) => {
    showNotificationPopup({
      message: 'Are you sure you want to delete this document type?',
      actionButton: { text: 'Delete', color: 'var(--red)' },
      onAction: async () => {
        try {
          const response = await httpClient.delete(import.meta.env.VITE_API_URL + `/api/document-types/${documentTypeId}`)

          if (response.status === 200) {
            setDocumentTypesByScope(prev => ({
              ...prev,
              [scope]: (prev[scope] || []).filter(item => item.id !== documentTypeId),
            }))
            toast.success('Document type deleted successfully')
          } else {
            toast.error(response.data?.error || 'Failed to delete document type')
          }
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to delete document type')
        }
      },
    })
  }

  return (
    <div className='template'>
      <Header>
        {activeSubmenu === 'profile' ? (
          <Button className='ui-button--header-right' variant='primary' size='sm' type='button' onClick={handleProfileHeaderAction} disabled={profileSaving}>
            {profileSaving ? 'SAVING...' : isEditingProfile ? 'SAVE' : 'EDIT'}
          </Button>
        ) : null}
      </Header>

      <MenuBar />

      <div className='template__body settings-page__body' id='body-settings'>
        <div className='body__section settings-page__submenu'>
          <div className='settings-page__submenu-list'>
            {SETTINGS_SUBMENUS.map(item => (
              <button
                key={item.id}
                className={`settings-page__submenu-item ${activeSubmenu === item.id ? 'active' : ''}`}
                onClick={() => setActiveSubmenu(item.id)}
                type='button'
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className='body__section settings-page__content'>
          {activeSubmenu === 'profile' && (
            <div className='settings-page__panel-grid'>
              <div className='form__section settings-page__card'>
                <h1>User details</h1>

                <div className='input_field'>
                  <label className='input_field__label' htmlFor='settings-first-name'>First name</label>
                  <input
                    className='input_field__input_box--text'
                    id='settings-first-name'
                    type='text'
                    value={profileForm.first_name}
                    readOnly={!isEditingProfile}
                    onChange={event => handleProfileFieldChange('first_name', event.currentTarget.value)}
                  />
                </div>

                <div className='input_field'>
                  <label className='input_field__label' htmlFor='settings-last-name'>Last name</label>
                  <input
                    className='input_field__input_box--text'
                    id='settings-last-name'
                    type='text'
                    value={profileForm.last_name}
                    readOnly={!isEditingProfile}
                    onChange={event => handleProfileFieldChange('last_name', event.currentTarget.value)}
                  />
                </div>

                <div className='input_field'>
                  <label className='input_field__label' htmlFor='settings-email'>Email</label>
                  <input
                    className='input_field__input_box--text'
                    id='settings-email'
                    type='email'
                    value={profileForm.email}
                    readOnly={!isEditingProfile}
                    onChange={event => handleProfileFieldChange('email', event.currentTarget.value)}
                  />
                </div>

                <div className='input_field'>
                  <label className='input_field__label' htmlFor='settings-role'>Role</label>
                  <input
                    className='input_field__input_box--text'
                    id='settings-role'
                    type='text'
                    value={profileForm.type}
                    readOnly
                  />
                </div>
              </div>

              <div className='form__section settings-page__card settings-page__profile-summary'>
                <h1>Account overview</h1>

                <div className='settings-page__summary-list'>
                  <div className='settings-page__summary-item'>
                    <span>Name</span>
                    <strong>{`${profileForm.first_name} ${profileForm.last_name}`.trim() || '-'}</strong>
                  </div>
                  <div className='settings-page__summary-item'>
                    <span>Email</span>
                    <strong>{profileForm.email || '-'}</strong>
                  </div>
                  <div className='settings-page__summary-item'>
                    <span>Role</span>
                    <strong>{profileForm.type || '-'}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSubmenu === 'data' && (
            <div className='settings-page__data-layout'>
              <div className='form__section settings-page__card settings-page__data-submenu-card'>
                <div className='settings-page__data-submenu-list'>
                  {DATA_SUBMENUS.map(item => (
                    <button
                      key={item.id}
                      className={`settings-page__data-submenu-item ${activeDataSubmenu === item.id ? 'active' : ''}`}
                      onClick={() => setActiveDataSubmenu(item.id)}
                      type='button'
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className='form__section settings-page__card settings-page__data-content-card'>
                {activeDataSubmenu === 'tags' && (
                  <>
                    <h1>Tags</h1>

                    <div className='settings-page__create-row'>
                      <input
                        className='input_field__input_box--text'
                        type='text'
                        placeholder='Create a new tag'
                        value={newTagValue}
                        onChange={event => setNewTagValue(event.currentTarget.value)}
                      />
                      <Button variant='primary' size='sm' className='settings-page__inline-button' type='button' onClick={handleCreateTag}>
                        <AddOutlinedIcon />
                        Create
                      </Button>
                    </div>

                    <div className='settings-page__tag-toolbar'>
                      <div className='settings-page__filter-group'>
                        {TAG_FILTERS.map(filterItem => (
                          <button
                            key={filterItem.id}
                            className={`settings-page__filter-button ${tagFilter === filterItem.id ? 'active' : ''}`}
                            type='button'
                            onClick={() => setTagFilter(filterItem.id)}
                          >
                            {filterItem.label}
                          </button>
                        ))}
                      </div>

                      <div className='settings-page__selection-summary'>
                        {selectedTagIds.length > 0 ? `${selectedTagIds.length} selected` : `${sortedTags.length} tags`}
                        {Object.keys(duplicateTagGroups).length > 0 ? ` • ${Object.keys(duplicateTagGroups).length} duplicate groups` : ''}
                      </div>
                    </div>

                    {selectedTagIds.length >= 2 ? (
                      <div className='settings-page__merge-panel'>
                        <div className='settings-page__merge-copy'>
                          <strong>Merge selected tags</strong>
                          <span>Click one of the selected rows to choose the surviving tag. Data from the other selected tags will be transferred first.</span>
                        </div>

                        <div className='settings-page__merge-controls'>
                          <Button variant='primary' size='sm' className='settings-page__inline-button' type='button' onClick={handleRequestMerge} disabled={mergeSaving}>
                            {mergeSaving ? 'Merging...' : 'Merge'}
                          </Button>

                          <button className='settings-page__text-button' type='button' onClick={() => setSelectedTagIds([])}>
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <div className='settings-page__data-list'>
                      {loadingData ? <p className='settings-page__empty'>Loading tags...</p> : null}
                      {!loadingData && sortedTags.length === 0 ? <p className='settings-page__empty'>No tags available yet.</p> : null}
                      {!loadingData && sortedTags.length > 0 && filteredSortedTags.length === 0 ? <p className='settings-page__empty'>No duplicate tags found.</p> : null}

                      {filteredSortedTags.map(tag => (
                        <div
                          className={`settings-page__data-row settings-page__data-row--tag ${selectedTagIds.some(tagId => isSameId(tagId, tag.id)) ? 'is-selected' : ''} ${isSameId(mergeTargetTagId, tag.id) ? 'is-survivor' : ''}`}
                          key={tag.id}
                          onClick={() => {
                            if (selectedTagIds.some(tagId => isSameId(tagId, tag.id)) && selectedTagIds.length >= 2 && editingTagId !== tag.id) {
                              setMergeTargetTagId(tag.id)
                            }
                          }}
                        >
                          <label className='settings-page__checkbox' aria-label={`Select ${tag.tag}`}>
                            <input
                              type='checkbox'
                              checked={selectedTagIds.some(tagId => isSameId(tagId, tag.id))}
                              onChange={() => handleToggleTagSelection(tag.id)}
                              onClick={event => event.stopPropagation()}
                            />
                          </label>

                          {editingTagId === tag.id ? (
                            <input
                              className='input_field__input_box--text'
                              type='text'
                              value={editingTagValue}
                              onChange={event => setEditingTagValue(event.currentTarget.value)}
                              onClick={event => event.stopPropagation()}
                            />
                          ) : (
                            <div className='settings-page__data-value'>
                              {tag.tag}
                              {duplicateTagIds.has(tag.id) ? <span className='settings-page__duplicate-badge'>Duplicate</span> : null}
                              {selectedTagIds.some(tagId => isSameId(tagId, tag.id)) && selectedTagIds.length >= 2 ? (
                                <span className={`settings-page__survivor-badge ${isSameId(mergeTargetTagId, tag.id) ? 'active' : ''}`}>
                                  {isSameId(mergeTargetTagId, tag.id) ? 'Survivor' : 'Select as survivor'}
                                </span>
                              ) : null}
                            </div>
                          )}

                          <div className='settings-page__row-actions'>
                            {editingTagId === tag.id ? (
                              <>
                                 <Button variant='icon' tone='green' iconOnly type='button' onClick={(event) => { event.stopPropagation(); handleSaveTag(tag.id) }} aria-label='Save tag'><CheckOutlinedIcon /></Button>
                                 <Button variant='icon' iconOnly type='button' onClick={(event) => { event.stopPropagation(); setEditingTagId(null); setEditingTagValue('') }} aria-label='Cancel editing tag'><CloseOutlinedIcon /></Button>
                              </>
                            ) : (
                              <>
                                 <Button variant='icon' tone='blue' iconOnly type='button' onClick={(event) => { event.stopPropagation(); handleStartEditTag(tag) }} aria-label='Edit tag'><EditOutlinedIcon /></Button>
                                 <Button variant='icon' tone='red' iconOnly type='button' onClick={(event) => { event.stopPropagation(); handleDeleteTag(tag.id) }} aria-label='Delete tag'><DeleteOutlineOutlinedIcon /></Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {activeDataSubmenu === 'company-functions' && (
                  <>
                    <h1>Company functions</h1>

                    <div className='settings-page__create-row'>
                      <input
                        className='input_field__input_box--text'
                        type='text'
                        placeholder='Create a new company function'
                        value={newCompanyFunctionValue}
                        onChange={event => setNewCompanyFunctionValue(event.currentTarget.value)}
                      />
                      <Button variant='primary' size='sm' className='settings-page__inline-button' type='button' onClick={handleCreateCompanyFunction}>
                        <AddOutlinedIcon />
                        Create
                      </Button>
                    </div>

                    <div className='settings-page__data-list'>
                      {loadingData ? <p className='settings-page__empty'>Loading company functions...</p> : null}
                      {!loadingData && sortedCompanyFunctions.length === 0 ? <p className='settings-page__empty'>No company functions available yet.</p> : null}

                      {sortedCompanyFunctions.map(item => (
                        <div className='settings-page__data-row' key={item.id}>
                          {editingFunctionId === item.id ? (
                            <input
                              className='input_field__input_box--text'
                              type='text'
                              value={editingFunctionValue}
                              onChange={event => setEditingFunctionValue(event.currentTarget.value)}
                            />
                          ) : (
                            <div className='settings-page__data-value'>{item.function}</div>
                          )}

                          <div className='settings-page__row-actions'>
                            {editingFunctionId === item.id ? (
                              <>
                                 <Button variant='icon' tone='green' iconOnly type='button' onClick={() => handleSaveCompanyFunction(item.id)} aria-label='Save company function'><CheckOutlinedIcon /></Button>
                                 <Button variant='icon' iconOnly type='button' onClick={() => { setEditingFunctionId(null); setEditingFunctionValue('') }} aria-label='Cancel editing company function'><CloseOutlinedIcon /></Button>
                              </>
                            ) : (
                              <>
                                 <Button variant='icon' tone='blue' iconOnly type='button' onClick={() => handleStartEditFunction(item)} aria-label='Edit company function'><EditOutlinedIcon /></Button>
                                 <Button variant='icon' tone='red' iconOnly type='button' onClick={() => handleDeleteCompanyFunction(item.id)} aria-label='Delete company function'><DeleteOutlineOutlinedIcon /></Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {activeDataSubmenu === 'document-types' && (
                  <>
                    <h1>Document types</h1>

                    <div className='settings-page__create-row settings-page__create-row--document-types'>
                      <PopupDropdown
                        options={DOCUMENT_TYPE_SCOPES}
                        value={newDocumentTypeScope}
                        onChange={setNewDocumentTypeScope}
                        title='Linked to'
                        searchBar={false}
                        formatType='input-field'
                      />
                      <input
                        className='input_field__input_box--text'
                        type='text'
                        placeholder='Create a new document type'
                        value={newDocumentTypeValues[newDocumentTypeScope?.id || 'product'] || ''}
                        onChange={event => setNewDocumentTypeValues(prev => ({ ...prev, [newDocumentTypeScope?.id || 'product']: event.currentTarget.value }))}
                      />
                      <Button variant='primary' size='sm' className='settings-page__inline-button' type='button' onClick={() => handleCreateDocumentType(newDocumentTypeScope?.id || 'product')}>
                        <AddOutlinedIcon />
                        Create
                      </Button>
                    </div>

                    <div className='settings-page__data-list'>
                      {loadingData ? <p className='settings-page__empty'>Loading document types...</p> : null}
                      {!loadingData && allSortedDocumentTypes.length === 0 ? <p className='settings-page__empty'>No document types available yet.</p> : null}

                      {allSortedDocumentTypes.map(item => (
                        <div className='settings-page__data-row settings-page__data-row--document-type' key={item.id}>
                          {editingDocumentTypeId === item.id ? (
                            <input
                              className='input_field__input_box--text'
                              type='text'
                              value={editingDocumentTypeValue}
                              onChange={event => setEditingDocumentTypeValue(event.currentTarget.value)}
                            />
                          ) : (
                            <div className='settings-page__data-value'>{item.name}</div>
                          )}

                          <div className='settings-page__data-meta-label'>
                              <Label
                                title={item.scopeLabel}
                                labelColor={DOCUMENT_TYPE_SCOPE_LABEL_COLORS[item.scope] || 'gray'}
                              maxWidth='140px'
                            />
                          </div>

                          <div className='settings-page__row-actions'>
                            {editingDocumentTypeId === item.id ? (
                              <>
                                 <Button variant='icon' tone='green' iconOnly type='button' onClick={() => handleSaveDocumentType(item.scope, item.id)} aria-label='Save document type'><CheckOutlinedIcon /></Button>
                                 <Button variant='icon' iconOnly type='button' onClick={() => { setEditingDocumentTypeId(null); setEditingDocumentTypeValue('') }} aria-label='Cancel editing document type'><CloseOutlinedIcon /></Button>
                              </>
                            ) : (
                              <>
                                 <Button variant='icon' tone='blue' iconOnly type='button' onClick={() => handleStartEditDocumentType(item)} aria-label='Edit document type'><EditOutlinedIcon /></Button>
                                 <Button variant='icon' tone='red' iconOnly type='button' onClick={() => handleDeleteDocumentType(item.scope, item.id)} aria-label='Delete document type'><DeleteOutlineOutlinedIcon /></Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SettingsPage
